/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    Link as ChakraLink,
    Icon,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import PropTypes from 'prop-types'
import React from 'react'
import {NavLink as NavSPALink, Link as SPALink} from 'react-router-dom'

import {iconNames, styleConfigChakraKeys} from '../../constants'

const LinkType = ({
    href,
    externalUrl,
    type,
    useNavLink,
    isExternal,
    text,
    icon,
    iconStyles = {},
    iconComponent,
    children,
    buttonStyles = {},
    openLinkInNewTab,
    ...props
}) => {
    const {buildUrl} = useMultiSite()
    const updatedHref = isExternal ? externalUrl : buildUrl(href)
    const styles = useMultiStyleConfig(styleConfigChakraKeys.linkType)

    return (
        <>
            {isExternal ? (
                <ChakraLink
                    href={updatedHref}
                    isExternal={openLinkInNewTab}
                    {...styles[type]}
                    {...buttonStyles}
                    {...props}
                >
                    {text && text}
                    {icon && <Icon as={iconNames[icon]} {...iconStyles} />}
                    {iconComponent && iconComponent}
                    {children}
                </ChakraLink>
            ) : (
                <ChakraLink
                    as={useNavLink ? NavSPALink : SPALink}
                    to={updatedHref}
                    isExternal={openLinkInNewTab}
                    {...styles[type]}
                    {...(useNavLink && {exact: true})}
                    {...buttonStyles}
                    {...props}
                >
                    {text && text}
                    {icon && <Icon as={iconNames[icon]} {...iconStyles} />}
                    {iconComponent && iconComponent}
                    {children}
                </ChakraLink>
            )}
        </>
    )
}

LinkType.displayName = 'Link'

LinkType.propTypes = {
    href: PropTypes.string,
    externalUrl: PropTypes.string,
    useNavLink: PropTypes.bool,
    isExternal: PropTypes.bool,
    text: PropTypes.string,
    icon: PropTypes.string,
    iconStyles: PropTypes.object,
    iconComponent: PropTypes.object,
    children: PropTypes.node,
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'unstyled']),
    buttonStyles: PropTypes.object,
    openLinkInNewTab: PropTypes.bool
}

export default React.memo(LinkType)
